import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { supabase } from '@/lib/supabase'
import { useLanguage } from '@/lib/context/LanguageContext'
import { useTranslation } from 'react-i18next'
import {
  User,
  Mail,
  Phone,
  Lock,
  Shield,
  Bell,
  Globe,
  Loader2,
  CheckCircle,
  AlertCircle,
  MapPin,
  Languages
} from 'lucide-react'

export function SettingsPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [activeTab, setActiveTab] = useState('profile')
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const { t } = useTranslation()
  const { currentLanguage, changeLanguage } = useLanguage()
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
    region: user?.region || ''
  })
  
  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    orderUpdates: true,
    marketingEmails: false,
    newMessages: true
  })
  
  // Language and region settings
  const [languageSettings, setLanguageSettings] = useState({
    language: currentLanguage,
    region: user?.region || 'Haiti'
  })

  useEffect(() => {
    if (!user) {
      navigate('/login')
    } else {
      // Update form with user data when it becomes available
      setProfileForm({
        full_name: user.full_name || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        region: user.region || ''
      })
      
      setLanguageSettings(prev => ({
        ...prev,
        language: currentLanguage,
        region: user.region || 'Haiti'
      }))
    }
  }, [user, navigate, currentLanguage])

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return
    
    setLoading(true)

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: profileForm.full_name,
          phone_number: profileForm.phone_number,
          region: profileForm.region
        })
        .eq('id', user.id)

      if (error) throw error

      // Update local user state
      useAuthStore.getState().loadUser()

      setToastMessage(t('settings.profile.saveChanges'))
      setToastType('success')
      setShowToast(true)
    } catch (error) {
      console.error('Error updating profile:', error)
      setToastMessage(t('common.error'))
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return
    
    // Validate passwords
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setToastMessage('Les mots de passe ne correspondent pas')
      setToastType('error')
      setShowToast(true)
      return
    }
    
    if (passwordForm.newPassword.length < 6) {
      setToastMessage(t('settings.security.passwordRequirements'))
      setToastType('error')
      setShowToast(true)
      return
    }
    
    setLoading(true)

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      })

      if (error) throw error

      setToastMessage('Mot de passe mis à jour avec succès')
      setToastType('success')
      setShowToast(true)
      
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (error) {
      console.error('Error updating password:', error)
      setToastMessage('Erreur lors de la mise à jour du mot de passe')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleNotificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    setLoading(true)
    
    try {
      // In a real app, you would save these settings to the database
      // For now, we'll just simulate a successful update
      
      setTimeout(() => {
        setToastMessage('Préférences de notification mises à jour')
        setToastType('success')
        setShowToast(true)
        setLoading(false)
      }, 500)
    } catch (error) {
      console.error('Error updating notification settings:', error)
      setToastMessage('Erreur lors de la mise à jour des préférences')
      setToastType('error')
      setShowToast(true)
      setLoading(false)
    }
  }

  const handleLanguageSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return
    
    setLoading(true)
    
    try {
      // Update region in profile
      const { error } = await supabase
        .from('profiles')
        .update({
          region: languageSettings.region
        })
        .eq('id', user.id)

      if (error) throw error

      // Change language
      changeLanguage(languageSettings.language)

      // Update local user state
      useAuthStore.getState().loadUser()

      setToastMessage('Préférences de langue et région mises à jour')
      setToastType('success')
      setShowToast(true)
    } catch (error) {
      console.error('Error updating language settings:', error)
      setToastMessage('Erreur lors de la mise à jour des préférences')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                {t('settings.profile.fullName')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="full_name"
                  value={profileForm.full_name}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, full_name: e.target.value }))}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('settings.profile.email')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  value={profileForm.email}
                  disabled
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 sm:text-sm"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">{t('settings.profile.emailCannotBeChanged')}</p>
            </div>

            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700">
                {t('settings.profile.phoneNumber')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phone_number"
                  value={profileForm.phone_number}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, phone_number: e.target.value }))}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="region" className="block text-sm font-medium text-gray-700">
                {t('settings.profile.region')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="region"
                  value={profileForm.region}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, region: e.target.value }))}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder={t('settings.profile.regionPlaceholder')}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('settings.profile.saving')}
                  </>
                ) : (
                  t('settings.profile.saveChanges')
                )}
              </Button>
            </div>
          </form>
        )
      
      case 'security':
        return (
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                {t('settings.security.currentPassword')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                {t('settings.security.newPassword')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                  required
                  minLength={6}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">{t('settings.security.passwordRequirements')}</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                {t('settings.security.confirmPassword')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                  required
                />
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Shield className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    {t('settings.security.securityTips')}
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>{t('settings.security.tip1')}</li>
                      <li>{t('settings.security.tip2')}</li>
                      <li>{t('settings.security.tip3')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('settings.security.updating')}
                  </>
                ) : (
                  t('settings.security.updatePassword')
                )}
              </Button>
            </div>
          </form>
        )
      
      case 'notifications':
        return (
          <form onSubmit={handleNotificationSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="emailNotifications"
                    name="emailNotifications"
                    type="checkbox"
                    checked={notificationSettings.emailNotifications}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, emailNotifications: e.target.checked }))}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="emailNotifications" className="ml-3 block text-sm font-medium text-gray-700">
                    {t('settings.notifications.emailNotifications')}
                  </label>
                </div>
                <span className="text-sm text-gray-500">{t('settings.notifications.allNotifications')}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="orderUpdates"
                    name="orderUpdates"
                    type="checkbox"
                    checked={notificationSettings.orderUpdates}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, orderUpdates: e.target.checked }))}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="orderUpdates" className="ml-3 block text-sm font-medium text-gray-700">
                    {t('settings.notifications.orderUpdates')}
                  </label>
                </div>
                <span className="text-sm text-gray-500">{t('settings.notifications.statusAndDelivery')}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="marketingEmails"
                    name="marketingEmails"
                    type="checkbox"
                    checked={notificationSettings.marketingEmails}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, marketingEmails: e.target.checked }))}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="marketingEmails" className="ml-3 block text-sm font-medium text-gray-700">
                    {t('settings.notifications.marketingEmails')}
                  </label>
                </div>
                <span className="text-sm text-gray-500">{t('settings.notifications.promotionsAndOffers')}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="newMessages"
                    name="newMessages"
                    type="checkbox"
                    checked={notificationSettings.newMessages}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, newMessages: e.target.checked }))}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="newMessages" className="ml-3 block text-sm font-medium text-gray-700">
                    {t('settings.notifications.newMessages')}
                  </label>
                </div>
                <span className="text-sm text-gray-500">{t('settings.notifications.chatsAndNegotiations')}</span>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Bell className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    {t('settings.notifications.aboutNotifications')}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      {t('settings.notifications.notificationsHelp')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('settings.notifications.savePreferences')
                )}
              </Button>
            </div>
          </form>
        )
      
      case 'language':
        return (
          <form onSubmit={handleLanguageSubmit} className="space-y-6">
            <div>
              <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                {t('settings.language.language')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Globe className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="language"
                  value={languageSettings.language}
                  onChange={(e) => setLanguageSettings(prev => ({ ...prev, language: e.target.value as 'fr' | 'en' | 'ht' }))}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="ht">Kreyòl Ayisyen</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="region" className="block text-sm font-medium text-gray-700">
                {t('settings.language.region')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="region"
                  value={languageSettings.region}
                  onChange={(e) => setLanguageSettings(prev => ({ ...prev, region: e.target.value }))}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder={t('settings.language.regionPlaceholder')}
                />
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Languages className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    {t('settings.language.regionalPreferences')}
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      {t('settings.language.regionalPreferencesHelp')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('settings.language.savePreferences')
                )}
              </Button>
            </div>
          </form>
        )
      
      default:
        return null
    }
  }

  if (!user) {
    return null // Redirect handled in useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="max-w-7xl mx-auto">
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              {t('settings.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('settings.subtitle')}
            </p>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="border-b border-gray-200">
            <div className="sm:hidden">
              <select
                id="tabs"
                name="tabs"
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
              >
                <option value="profile">{t('settings.tabs.profile')}</option>
                <option value="security">{t('settings.tabs.security')}</option>
                <option value="notifications">{t('settings.tabs.notifications')}</option>
                <option value="language">{t('settings.tabs.language')}</option>
              </select>
            </div>
            <div className="hidden sm:block">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`${
                    activeTab === 'profile'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  <User className="h-5 w-5 inline mr-2" />
                  {t('settings.tabs.profile')}
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`${
                    activeTab === 'security'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  <Shield className="h-5 w-5 inline mr-2" />
                  {t('settings.tabs.security')}
                </button>
                <button
                  onClick={() => setActiveTab('notifications')}
                  className={`${
                    activeTab === 'notifications'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  <Bell className="h-5 w-5 inline mr-2" />
                  {t('settings.tabs.notifications')}
                </button>
                <button
                  onClick={() => setActiveTab('language')}
                  className={`${
                    activeTab === 'language'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  <Globe className="h-5 w-5 inline mr-2" />
                  {t('settings.tabs.language')}
                </button>
              </nav>
            </div>
          </div>
          
          <div className="px-4 py-5 sm:p-6">
            {renderTabContent()}
          </div>
        </div>
        
        {/* Account Deletion Section */}
        <div className="mt-10 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('settings.deleteAccount.title')}
            </h3>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>
                {t('settings.deleteAccount.warning')}
              </p>
            </div>
            <div className="mt-5">
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400 w-full sm:w-auto"
                onClick={() => {
                  setToastMessage(t('settings.deleteAccount.disabledMessage'))
                  setToastType('error')
                  setShowToast(true)
                }}
              >
                <AlertCircle className="h-4 w-4 mr-2" />
                {t('settings.deleteAccount.deleteButton')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}